import { LoadableLabel } from 'src/forms/CreditStudentCoins';
import { FC, ReactElement, useMemo } from 'react';
import { Formik, FormikValues } from 'formik';
import classNames from 'classnames';

export type ButtonProps<T extends FormikValues = any> = {
    className?: classNames.Argument | classNames.Argument[];
    disabled?: boolean;
    loading?: boolean;
    label?: string;
    onClick?: () => void;
    autoFocus?: boolean;
    formik?: Formik<T>;
    submit?: boolean;
};

export type ButtonPropsWithInternalSettings = ButtonProps & {
    _internal?: {
        loadingRingColor?: string;
    };
};

export const Button: FC<ButtonPropsWithInternalSettings> = (
    props: ButtonPropsWithInternalSettings
): ReactElement => {
    const {
        _internal = {},
        formik,
        autoFocus = false,
        disabled = false,
        loading = false,
        submit = false,
        className,
        label,
    } = props;

    const isDisabled: boolean = useMemo(() => {
        if (disabled) return true;
        if (!formik) return false;

        return !formik.isValid || !formik.dirty;
    }, [formik?.isValid, formik?.dirty, disabled]);

    const isLoading: boolean = useMemo(() => {
        if (loading) return true;
        if (!formik) return false;

        return formik.isSubmitting;
    }, [formik?.isSubmitting, loading]);

    return (
        <button
            className={classNames(className, {
                loading: isLoading,
                disabled: isLoading || isDisabled,
            })}
            disabled={isLoading || isDisabled}
            type={submit ? 'submit' : 'button'}
            onClick={props.onClick}
            autoFocus={autoFocus}
        >
            <LoadableLabel
                isLoading={isLoading}
                color={_internal.loadingRingColor}
            >
                <p>{label}</p>
            </LoadableLabel>
        </button>
    );
};
